import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';

export type FeedbackType = 'bug_report' | 'feature_request' | 'general_feedback' | 'other';
export type FeedbackStatus = 'new' | 'under_review' | 'acknowledged' | 'resolved' | 'wont_fix';

export interface PlatformFeedback {
  id: string;
  user_id: string;
  organization_id: string;
  feedback_type: FeedbackType;
  subject: string;
  message: string;
  attachment_urls: string[];
  status: FeedbackStatus;
  admin_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface FeedbackSubmission {
  feedback_type: FeedbackType;
  subject: string;
  message: string;
  files?: File[];
}

export interface FeedbackStats {
  new: number;
  under_review: number;
  acknowledged: number;
  resolved: number;
  wont_fix: number;
  total: number;
}

export interface FeedbackFilters {
  status?: FeedbackStatus | 'all';
  type?: FeedbackType | 'all';
}

export function usePlatformFeedback() {
  const { user, organization } = useAuth();
  const [myFeedback, setMyFeedback] = useState<PlatformFeedback[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchMyFeedback = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('platform_feedback')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMyFeedback((data || []) as unknown as PlatformFeedback[]);
    } catch (error) {
      console.error('Error fetching feedback:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchMyFeedback();
  }, [fetchMyFeedback]);

  const uploadAttachments = async (files: File[], feedbackId: string): Promise<string[]> => {
    if (!user || files.length === 0) return [];

    const uploadedUrls: string[] = [];

    for (const file of files) {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${feedbackId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('feedback-attachments')
        .upload(fileName, file);

      if (uploadError) {
        console.error('Error uploading file:', uploadError);
        throw new Error(`Failed to upload ${file.name}`);
      }

      uploadedUrls.push(fileName);
    }

    return uploadedUrls;
  };

  const submitFeedback = async (submission: FeedbackSubmission): Promise<boolean> => {
    if (!user || !organization?.id) {
      toast.error('You must be logged in to submit feedback');
      return false;
    }

    setSubmitting(true);
    try {
      // First create the feedback record
      const { data: feedback, error: insertError } = await supabase
        .from('platform_feedback')
        .insert({
          user_id: user.id,
          organization_id: organization.id,
          feedback_type: submission.feedback_type,
          subject: submission.subject,
          message: submission.message,
          attachment_urls: [],
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Upload attachments if any
      if (submission.files && submission.files.length > 0) {
        const attachmentUrls = await uploadAttachments(submission.files, feedback.id);
        
        // Update the feedback record with attachment URLs
        const { error: updateError } = await supabase
          .from('platform_feedback')
          .update({ attachment_urls: attachmentUrls })
          .eq('id', feedback.id);

        if (updateError) {
          console.error('Error updating attachment URLs:', updateError);
        }
      }

      // Trigger notification edge function
      try {
        await invokeEdgeFunction('feedback-notify', {
          body: { feedback_id: feedback.id },
          callerOperation: 'usePlatformFeedback.notify',
        });
      } catch (notifyError) {
        console.warn('Failed to send notification:', notifyError);
        // Don't fail the submission if notification fails
      }

      toast.success('Feedback submitted successfully');
      await fetchMyFeedback();
      return true;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  return {
    myFeedback,
    loading,
    submitting,
    submitFeedback,
    refreshFeedback: fetchMyFeedback,
  };
}

export function useAdminFeedback(filters: FeedbackFilters = {}) {
  const [feedback, setFeedback] = useState<PlatformFeedback[]>([]);
  const [stats, setStats] = useState<FeedbackStats>({
    new: 0,
    under_review: 0,
    acknowledged: 0,
    resolved: 0,
    wont_fix: 0,
    total: 0,
  });
  const [loading, setLoading] = useState(false);

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('platform_feedback')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      if (filters.type && filters.type !== 'all') {
        query = query.eq('feedback_type', filters.type);
      }

      const { data, error } = await query;

      if (error) throw error;
      setFeedback((data || []) as unknown as PlatformFeedback[]);
    } catch (error) {
      console.error('Error fetching admin feedback:', error);
    } finally {
      setLoading(false);
    }
  }, [filters.status, filters.type]);

  const fetchStats = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('platform_feedback')
        .select('status');

      if (error) throw error;

      const counts: FeedbackStats = {
        new: 0,
        under_review: 0,
        acknowledged: 0,
        resolved: 0,
        wont_fix: 0,
        total: 0,
      };

      (data || []).forEach((item: { status: string }) => {
        counts.total++;
        const status = item.status as FeedbackStatus;
        if (status in counts) {
          counts[status]++;
        }
      });

      setStats(counts);
    } catch (error) {
      console.error('Error fetching feedback stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchFeedback();
    fetchStats();
  }, [fetchFeedback, fetchStats]);

  const updateStatus = async (id: string, status: FeedbackStatus): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('platform_feedback')
        .update({ 
          status,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;

      toast.success('Status updated');
      await fetchFeedback();
      await fetchStats();
      return true;
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
      return false;
    }
  };

  const updateAdminNotes = async (id: string, notes: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('platform_feedback')
        .update({ admin_notes: notes })
        .eq('id', id);

      if (error) throw error;

      toast.success('Notes saved');
      await fetchFeedback();
      return true;
    } catch (error) {
      console.error('Error updating notes:', error);
      toast.error('Failed to save notes');
      return false;
    }
  };

  const getAttachmentUrl = async (path: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase.storage
        .from('feedback-attachments')
        .createSignedUrl(path, 3600); // 1 hour expiry

      if (error) throw error;
      return data.signedUrl;
    } catch (error) {
      console.error('Error getting attachment URL:', error);
      return null;
    }
  };

  return {
    feedback,
    stats,
    loading,
    updateStatus,
    updateAdminNotes,
    getAttachmentUrl,
    refreshFeedback: fetchFeedback,
  };
}
